import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight, Phone, Leaf, MapPin } from "lucide-react";
import { REBATES_GUIDE } from "@/data/rebatesGuide";
import { PANEL_GUIDE } from "@/data/panelUpgradeGuide";
import { CITIES, CITY_ORDER } from "@/data/cities";
import { PANEL_CITIES, PANEL_CITY_ORDER } from "@/data/panelUpgradeCities";
import { BUSINESS } from "@/data/content";
import useSEO from "@/hooks/useSEO";
import { articleSchema, breadcrumbSchema, faqSchema } from "@/lib/schema";

const GUIDE_CONFIG = {
  ev: { guide: REBATES_GUIDE, cities: CITIES, order: CITY_ORDER, serviceSlug: "ev-charger-installation", label: "EV Charger", crumb: "BC EV Rebates 2026", kicker: "2026 Rebate Guide", url: "/guides/bc-ev-charger-rebates-2026" },
  panel: { guide: PANEL_GUIDE, cities: PANEL_CITIES, order: PANEL_CITY_ORDER, serviceSlug: "panel-upgrades", label: "Panel Upgrade", crumb: "Panel Upgrade Cost 2026", kicker: "2026 BC Cost Guide", url: "/guides/electrical-panel-upgrade-cost-bc-2026" },
};

export default function RebatesGuide({ variant = "ev" }) {
  const cfg = GUIDE_CONFIG[variant];
  const g = cfg.guide;
  useSEO({
    title: g.metaTitle,
    description: g.metaDesc,
    type: "article",
    jsonld: [
      articleSchema({
        headline: g.metaTitle,
        description: g.metaDesc,
        url: cfg.url,
        datePublished: "2026-01-15T09:00:00.000Z",
        author: "ElectroMax Systems",
        section: cfg.label,
      }),
      breadcrumbSchema([
        { name: "Home", href: "/" },
        { name: "Guides", href: "/" },
        { name: cfg.crumb, href: cfg.url },
      ]),
      g.faqs ? faqSchema(g.faqs) : null,
    ].filter(Boolean),
  });
  return (
    <div data-testid="rebates-guide-page">
      {/* Hero */}
      <section className="relative pt-36 md:pt-44 pb-16 md:pb-24 overflow-hidden grain">
        <div className="pointer-events-none absolute -top-40 -left-40 w-[560px] h-[560px] bg-[#E60000]/10 blur-[140px] rounded-full" />
        <div className="mx-auto max-w-[1000px] px-6 md:px-10 lg:px-16">
          <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-white/50 mb-8">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span>Guides</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">{cfg.crumb}</span>
          </div>
          <div className="text-xs tracking-[0.24em] uppercase text-[#E60000] font-bold mb-4 inline-flex items-center gap-2">
            <Leaf className="w-3.5 h-3.5" strokeWidth={2.5} /> {cfg.kicker}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-[68px] leading-[0.98] tracking-tighter font-black">
            {g.h1}
          </h1>
          <div className="mt-8 space-y-5 text-base md:text-lg text-white/80 leading-relaxed">
            {g.intro.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="border-t border-[#262626] bg-[#0A0A0A]">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10 lg:px-16 py-16 lg:py-24 space-y-16">
          {g.sections.map((s, idx) => (
            <article key={idx} className="border-l-2 border-[#E60000] pl-6 md:pl-10">
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#E60000] font-bold mb-3">
                Section {String(idx + 1).padStart(2, "0")}
              </div>
              <h2 className="font-display text-3xl md:text-4xl tracking-tighter font-bold leading-[1.05]">
                {s.h}
              </h2>
              {s.body && (
                <div className="mt-5 space-y-4 text-white/80 leading-relaxed">
                  {s.body.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              )}
              {s.items && (
                <ul className="mt-6 grid gap-3">
                  {s.items.map((it, i) => {
                    const isObj = typeof it === "object";
                    return (
                      <li key={i} className="bg-[#141414] border border-[#262626] p-4">
                        {isObj && it.h && (
                          <div className="font-display font-bold text-white mb-1">{it.h}</div>
                        )}
                        <span className="text-sm md:text-base text-white/80 leading-relaxed">
                          {isObj ? it.p : it}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
              {s.subsections && (
                <div className="mt-6 grid md:grid-cols-2 gap-4">
                  {s.subsections.map((sub, si) => (
                    <div key={si} className="border border-[#262626] bg-[#141414] p-5">
                      <div className="font-display font-bold text-lg text-white mb-3">{sub.h}</div>
                      <ul className="space-y-2 text-sm text-white/75 leading-relaxed">
                        {sub.items.map((li, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="text-[#E60000] mt-1.5 shrink-0">▪</span>
                            <span>{li}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
              {s.ordered && (
                <ol className="mt-6 space-y-3 counter-reset">
                  {s.ordered.map((li, i) => (
                    <li key={i} className="flex gap-4 border border-[#262626] bg-[#141414] p-4">
                      <span className="font-display text-2xl font-black text-[#E60000] shrink-0 leading-none">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-sm md:text-base text-white/80 leading-relaxed">{li}</span>
                    </li>
                  ))}
                </ol>
              )}
              {s.outro && (
                <p className="mt-6 text-white/80 leading-relaxed">{s.outro}</p>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-32 bg-[#F4F4F5] text-[#0A0A0A] border-t border-[#E4E4E7]">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10 lg:px-16">
          <div className="text-xs tracking-[0.24em] uppercase text-black/50 mb-4">— Frequently Asked Questions</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95]">
            Rebate questions,
            <br />
            <span className="text-black/40">answered plainly.</span>
          </h2>
          <div className="mt-12 divide-y divide-[#E4E4E7] border-y border-[#E4E4E7]">
            {g.faqs.map((f, i) => (
              <details key={i} data-testid={`guide-faq-${i}`} className="group py-6 [&_summary::-webkit-details-marker]:hidden">
                <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                  <span className="font-display text-lg md:text-xl font-bold text-black">{f.q}</span>
                  <span className="mt-1 shrink-0 inline-flex w-8 h-8 border border-[#E4E4E7] items-center justify-center group-open:bg-[#E60000] group-open:border-[#E60000] group-open:text-white transition-colors">
                    <span className="block relative w-3 h-3">
                      <span className="absolute inset-x-0 top-1/2 h-[2px] bg-current" />
                      <span className="absolute inset-y-0 left-1/2 w-[2px] bg-current group-open:hidden" />
                    </span>
                  </span>
                </summary>
                <p className="mt-4 text-sm md:text-base text-black/70 leading-relaxed max-w-3xl">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA + Cities */}
      <section className="py-20 border-t border-[#262626] bg-[#0A0A0A]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-10">
            <div className="md:col-span-8">
              <div className="text-xs tracking-[0.24em] uppercase text-[#E60000] font-bold mb-3">
                {g.closing.h}
              </div>
              <p className="text-lg md:text-xl text-white/85 leading-relaxed max-w-3xl">{g.closing.body}</p>
            </div>
            <div className="md:col-span-4 md:justify-self-end flex flex-wrap gap-3">
              <Link to="/#contact" className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold px-6 py-4 text-sm">
                Get a Free Quote <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a href={BUSINESS.phoneHref} className="inline-flex items-center gap-2 border border-[#262626] hover:border-white text-white font-bold px-6 py-4 text-sm">
                <Phone className="w-4 h-4" /> {BUSINESS.phone}
              </a>
            </div>
          </div>

          <div className="mt-12">
            <div className="text-xs tracking-[0.24em] uppercase text-white/60 mb-4">— City-Specific {cfg.label} Pages</div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
              {cfg.order.map((s) => {
                const c = cfg.cities[s];
                return (
                  <Link
                    key={s}
                    to={`/services/${cfg.serviceSlug}/${s}`}
                    data-testid={`guide-city-${s}`}
                    className="group border border-[#262626] hover:border-[#E60000] p-4 flex flex-col gap-1 transition-colors"
                  >
                    <MapPin className="w-3.5 h-3.5 text-[#E60000]" strokeWidth={2.5} />
                    <span className="mt-1 font-display text-lg font-bold group-hover:text-white transition-colors">
                      {c.city}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
